/**
 * @graphql cc
 */
import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ErrorDisplay } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';

import { ReadTimeComponent } from './ReadTimeComponent';
import { ReadTimePopupExperiment } from './ReadTimePopupExperiment';
import { getContentFeatureExtractedEventObject } from './readTimeUtils';
import { useReadTime } from './hooks/useReadTime';

export const OriginalReadTime = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	isLoomAIScriptExperimentEnabled = false,
}: {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	isLoomAIScriptExperimentEnabled?: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		adf,
		loading,
		error,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
		isEligibleForLoomAIScriptSection,
	} = useReadTime(contentId, undefined, isLoomAIScriptExperimentEnabled);

	useEffect(() => {
		if (!createAnalyticsEvent || !adf || loading || error || !contentId) {
			return;
		}
		createAnalyticsEvent(
			getContentFeatureExtractedEventObject({ contentType, contentId, adf, isInEditor: false }),
		).fire();
	}, [contentId, loading, error, contentType, createAnalyticsEvent, adf]);

	if (error) {
		return <ErrorDisplay error={error} />;
	}
	if (readTime || (showWordCount && wordCount !== null)) {
		if (fg('confluence_ai_page_read_time_popup')) {
			return (
				<ReadTimePopupExperiment
					contentId={contentId}
					isAbbreviatedReadTime={isAbbreviatedReadTime}
					isDetailsPanel={isDetailsPanel}
					showWordCount={showWordCount}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					wordCount={wordCount ?? 0}
					readTime={readTime}
					source="originalReadTime"
					isEligibleForLoomAIScriptSection={isEligibleForLoomAIScriptSection}
				/>
			);
		}

		return (
			<ReadTimeComponent
				readTime={readTime}
				wordCount={wordCount}
				pageId={contentId}
				isAbbreviatedReadTime={isAbbreviatedReadTime}
				isDetailsPanel={isDetailsPanel}
				showWordCount={showWordCount}
			/>
		);
	}

	return null;
};
